import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Divider from 'components/divider/Divider';
import {
  ArrowRightIcon,
  BackIcon,
  EstimateColorIcon,
  GreenFilledTickIcon,
  GreyStrokeInfoIcon,
  StarIcon
} from 'assets/icons';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import {
  CURRENT_ESTIMATE_FEATURES,
  SubscriptionDescription,
  SubscriptionProcessingStatus,
  UPCOMING_ESTIMATE_FEATURES
} from 'containers/estimates/constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { getNotifications, selectCurrentTeam, showNotifier } from 'containers/app/appSlice';
import { SubscriptionStatus } from 'containers/app/app.model';
import {
  useCancelSubscriptionMutation,
  useCompleteSubscriptionMutation,
  useInitiateSubscriptionMutation,
  useLazyGetCheckoutSessionQuery,
  useLazyGetSubscriptionPlansQuery,
  useRenewSubscriptionMutation
} from 'containers/estimates/api';
import { InitiateSubscriptionPayload } from 'containers/estimates/estimates.model';
import StripeSubscriptionsModal from 'containers/estimates/components/StripeSubscriptionsModal';
import CancelSubscriptionModal from './components/CancelSubscriptionModal';
import { useLazyGetMerchantTeamsQuery } from 'containers/app/api';
import routesPath from 'routes/RoutesPath';
import { ESTIMATES_TAGS, INTEGRATIONS_CARD_TYPE } from 'containers/integrations/constants';
import { openScanpayFAQInNewTab } from 'utils/urlUtils';
import { NotifierTypes } from 'constants/notifierConstants';
import { Loader } from 'components';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { useLazyGetIntegrationsQuery } from '../api';
import DateUtils from 'utils/dateUtils';

const EstimateIntegration: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(getNotifications);
  const {
    id: teamId,
    subscriptionStatus = SubscriptionStatus.NOT_SUBSCRIBED,
    integrationsEnabled,
    isSubscriptionCreatedFromWeb
  } = useAppSelector(selectCurrentTeam);

  const isSubscribed = subscriptionStatus === SubscriptionStatus.SUBSCRIBED;

  const [getSubscriptionPlans, { data: subscriptionPlans, isFetching: isFetchingSubscriptionPlans }] =
    useLazyGetSubscriptionPlansQuery();
  const [initiateSubscription, { isLoading: isInitiatingSubscription }] = useInitiateSubscriptionMutation();
  const [completeSubscription] = useCompleteSubscriptionMutation();
  const [getCheckoutSession] = useLazyGetCheckoutSessionQuery();
  const [renewSubscription, { isLoading: isRenewingSubscription, isSuccess: isRenewingSubscriptionSuccess }] =
    useRenewSubscriptionMutation();
  const [cancelSubscription, { isLoading: isCancellingSubscription, isSuccess: isCancellingSubscriptionSuccess }] =
    useCancelSubscriptionMutation();
  const [getMerchantTeams] = useLazyGetMerchantTeamsQuery();
  const [getIntegrationsQuery, { data: integrationsData, isSuccess: isIntegrationsSuccess }] =
    useLazyGetIntegrationsQuery();

  const [isFreeTrial, setIsFreeTrial] = useState(false);

  useEffect(() => {
    if (teamId && integrationsEnabled) {
      getSubscriptionPlans();
    }
    if (!integrationsEnabled) {
      navigate(routesPath.HOME);
    }
  }, [teamId, integrationsEnabled]);

  useEffect(() => {
    if (isRenewingSubscriptionSuccess) {
      getMerchantTeams();
    }
  }, [isRenewingSubscriptionSuccess]);

  useEffect(() => {
    if (isCancellingSubscriptionSuccess) {
      getMerchantTeams();
      handleShowNotifier('estimates-info', 'Subscription cancelled successfully', NotifierTypes.SUCCESS);
    }
  }, [isCancellingSubscriptionSuccess]);

  useEffect(() => {
    if (isSubscriptionCancelled && isSubscriptionCreatedFromWeb) getIntegrationsQuery({ teamId: teamId.toString() });
  }, [subscriptionStatus, isSubscriptionCreatedFromWeb]);

  const isSubscriptionCancelled = subscriptionStatus === SubscriptionStatus.SUBSCRIPTION_CANCELLED;

  const { name, id, price, trialDuration } =
    subscriptionPlans?.data?.find(item => item.description === SubscriptionDescription.ESTIMATE_ONLY) ?? {};

  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const [openCancelSubscriptionModal, setOpenCancelSubscriptionModal] = useState(false);
  const { integrations } = integrationsData ?? {};

  const handleOpenStripeSubscriptionModal = async () => {
    const initiateSubscriptionPayload: InitiateSubscriptionPayload = {
      subscriptionPlanId: id,
      subscriptionPlanName: name,
      type: 'WEB',
      status: SubscriptionProcessingStatus.INITIATED
    };
    await initiateSubscription({ teamId, payload: initiateSubscriptionPayload });
    setOpenSubscriptionModal(true);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleCloseSubscriptionModal = async (sessionId: string) => {
    await completeSubscription({
      teamId,
      payload: {
        subscriptionPlanId: id,
        subscriptionPlanName: name,
        type: 'WEB',
        status: SubscriptionProcessingStatus.CANCELED
      }
    });
    await getCheckoutSession({ teamId, sessionId }).unwrap();
    setOpenSubscriptionModal(false);
  };

  const handleCancelSubscription = async () => {
    await cancelSubscription({ teamId });
    setOpenCancelSubscriptionModal(false);
  };

  const handleRenewSubscription = async () => {
    const response = await renewSubscription({ teamId }).unwrap();
    if (response.data.createCheckoutSession) {
      handleOpenStripeSubscriptionModal();
      setIsFreeTrial(false);
    } else {
      handleShowNotifier('estimates-info', 'Subscription renewed successfully', NotifierTypes.SUCCESS);
    }
  };

  const handleCancelSubscriptionClick = () => {
    setOpenCancelSubscriptionModal(true);
  };

  const getSubscriptionButtonState = () => {
    const status = subscriptionStatus.toUpperCase();
    switch (status) {
      case SubscriptionStatus.NOT_SUBSCRIBED:
        return `Try free for ${trialDuration} day${trialDuration > 1 ? 's' : ''}`;
      case SubscriptionStatus.SUBSCRIPTION_EXPIRED:
      case SubscriptionStatus.SUBSCRIPTION_CANCELLED:
        return 'Renew subscription';
      case SubscriptionStatus.SUBSCRIBED:
        return 'Cancel subscription';
      default:
        return '';
    }
  };

  const handleShowNotifier = (notifierId: string, infoMessage: string, type: NotifierTypes = NotifierTypes.WARNING) => {
    const existingNotification = notifications.find(notification => notification.id === notifierId);
    if (existingNotification) return;

    dispatch(
      showNotifier({
        id: notifierId,
        message: {
          primaryMessage: infoMessage,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  const handleSubscriptionButtonClick = () => {
    if (isSubscriptionPrimaryButtonDisabled()) {
      !(isSubscriptionCancelled && isSubscriptionCreatedFromWeb) &&
        handleShowNotifier(
          'estimates-info',
          `To manage or cancel your subscription, please use the same 
        app store / play store where you initially subscribed`
        );
      return;
    }
    const status = subscriptionStatus.toUpperCase();
    switch (status) {
      case SubscriptionStatus.NOT_SUBSCRIBED:
        setIsFreeTrial(true);
        handleOpenStripeSubscriptionModal();
        break;
      case SubscriptionStatus.SUBSCRIPTION_EXPIRED:
      case SubscriptionStatus.SUBSCRIPTION_CANCELLED:
        handleRenewSubscription();
        break;
      case SubscriptionStatus.SUBSCRIBED:
        handleCancelSubscriptionClick();
        break;
      default:
        break;
    }
  };

  const isSubscriptionPrimaryButtonDisabled = () => {
    if (isSubscriptionCancelled && isSubscriptionCreatedFromWeb) {
      return true;
    } else if (isSubscribed || isSubscriptionCancelled) {
      return !isSubscriptionCreatedFromWeb;
    }
    return false;
  };

  if (isFetchingSubscriptionPlans) {
    return <Loader />;
  }

  const getExpiryDate = () => {
    let formattedDate = null;
    if (isIntegrationsSuccess) {
      const date = integrations?.find(item => item?.name === INTEGRATIONS_CARD_TYPE.Estimates)?.expiry;
      formattedDate = date && DateUtils.getDateInFormat({ date: new Date(date), dateFormat: 'MMM dd, yyyy' });
    }
    return formattedDate;
  };

  return (
    <div className="h-full w-full pl-16">
      <div className="flex w-full items-center pt-8">
        <div className="mr-4 cursor-pointer" onClick={handleBackClick}>
          <BackIcon />
        </div>
      </div>
      <div className="w-3/5 py-10">
        <div className="flex items-center gap-3">
          <div className="rounded-lg border border-borderGray p-[6px]">
            <EstimateColorIcon width={28} height={28} />
          </div>
          <div className="text-sbase font-semibold text-primaryText">Estimates</div>
        </div>
        <div className="mt-5 text-sbase text-primaryText">
          Easily create and send estimates on the go with estimates. Subscription required.
        </div>
        <div className="mt-2 mb-5 text-[17px] font-bold text-primaryText">
          {getAmountWithCurrency(price / 100)} per month subscription fees
        </div>
        <div className="flex gap-[10px]">
          {ESTIMATES_TAGS.map((tag, index) => (
            <div className="w-fit rounded-lg bg-secondaryBtn px-2 py-1 text-[13px] text-accentText" key={tag + index}>
              {tag}
            </div>
          ))}
        </div>
        <CustomButton
          showDisabledReason={isSubscriptionPrimaryButtonDisabled()}
          onClick={handleSubscriptionButtonClick}
          id="subscription-state"
          className="mt-5 h-10 min-w-[160px]"
          type={isSubscribed ? ButtonType.ALERT : ButtonType.PRIMARY}
          isLoading={isRenewingSubscription || isCancellingSubscription || isInitiatingSubscription}>
          {getSubscriptionButtonState()}
        </CustomButton>
        {isSubscriptionCancelled && isSubscriptionCreatedFromWeb && (
          <div className="mt-2 flex items-center gap-1 text-sbase text-primaryText">
            <div>
              <GreyStrokeInfoIcon />
            </div>
            The subscription can only be renewed once the current subscription period ends
            {getExpiryDate() ? ` (after ${getExpiryDate()})` : ''}
          </div>
        )}
        <Divider variant="fullWidth" className="my-5 border-borderGray" />
        <div className="text-[17px] font-semibold text-primaryText">Description</div>
        <div className="my-5 text-sbase text-primaryText">
          Easily create and send estimates on the go with estimates.
        </div>
        <div className="text-[15px] font-semibold text-primaryText">Currently available features</div>
        <ul className="mt-[14px] text-sbase font-semibold text-primaryText">
          {CURRENT_ESTIMATE_FEATURES.map((feature, index) => (
            <li className="mb-2 flex items-center gap-2 p-1 font-medium" key={feature + index}>
              <GreenFilledTickIcon width={12} height={12} className="shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
        <div className="mt-6 text-[15px] font-semibold text-primaryText">Upcoming features</div>
        <ul className="mt-[14px] text-sbase font-semibold text-primaryText">
          {UPCOMING_ESTIMATE_FEATURES.map((feature, index) => (
            <li className="mb-2 flex items-center gap-2 p-1 font-medium" key={feature + index}>
              <StarIcon width={12} height={12} className="shrink-0" />
              {feature}
            </li>
          ))}
        </ul>

        <div className="mt-8 mb-5 text-[17px] font-semibold text-primaryText">Additional details</div>
        <div className="flex cursor-pointer items-center gap-1" onClick={() => openScanpayFAQInNewTab('')}>
          <div className="text-sbase font-semibold text-secondary">View FAQs on our website</div>
          <div className="mt-0.5">
            <ArrowRightIcon height={10} />
          </div>
        </div>
      </div>
      {openSubscriptionModal && (
        <StripeSubscriptionsModal
          handleClose={handleCloseSubscriptionModal}
          isFreeTrial={isFreeTrial}
          subscriptionPlanName={name}
        />
      )}
      {openCancelSubscriptionModal && (
        <CancelSubscriptionModal
          isOpen={openCancelSubscriptionModal}
          handleClose={() => setOpenCancelSubscriptionModal(false)}
          handleCancelSubscription={handleCancelSubscription}
          isLoading={isCancellingSubscription}
        />
      )}
    </div>
  );
};

export default EstimateIntegration;
